<script lang="ts">
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { fade } from 'svelte/transition';

    import type { NotificationSystemContext } from './NotificationSystemContext';
    import { NotificationSystem } from './NotificationSystem';
    import ProgressBar from '../../shared/ProgressBar.svelte';
    import ButtonSmallBlueRounded from '../../shared/ButtonSmallBlueRounded.svelte';
    import { CalcRouteProgress } from './CalcRouteProgress';
    import { jobs, routes } from '../../../stores/store';
    import { app } from '../../../stores/app';
    import { filter } from '../../../stores/filter';
    import { Navigation } from '../../utilities/Navigation';
    import { CustomFilterManagerIO } from '../../utilities/FilterManagerIO';
    import { get } from 'svelte/store';
    import { api } from '../../../stores/api';
    import { FilterManager } from '../telematics/components/FilterByParameters/FilterManager';
    import Filter from '../telematics/components/FilterByParameters/Filter';
    import FilterLastUsed from '../telematics/components/FilterByParameters/FilterLastUsed';
    import { Umami } from '../../utilities/Umami';
    import { canWrite, writeDisabledTooltip } from '../../shared/Permissions/writeAccess';

    let context: NotificationSystemContext;
    let progressPercent: number = 0;

    let crp: CalcRouteProgress = new CalcRouteProgress();

    let wasBusy: boolean = false;

    let transactionLimitExceeded: string = '';

    const reloadData = (ctx: NotificationSystemContext) => {
        context = ctx;

        console.log('reloadData', ctx);

        // crp
        crp = crp.setContext(ctx);
        if (crp.isCalculationInProgress()) {
            progressPercent = crp.getProgressPercent();
        }
        if (crp.isCalculationInProgress() && ctx?.countRoutesInCalculation === 0 && wasBusy) {
            console.log('CalcRouteProgress stopCalculation (nothing to do)');
            crp = crp.stopCalculation();
        }
        if (crp.isCalculationInProgress()) {
            console.log('CalcRouteProgress isCalculationInProgress');
            /*setTimeout(() => {
                $NotificationSystem.reload();
            }, 5000);*/
            wasBusy = true;
            $NotificationSystem.changeIntervalSeconds(3);
        } else {
            if (wasBusy) {
                $NotificationSystem.changeIntervalSeconds(30);
                wasBusy = false;
            }
            console.log('CalcRouteProgress stopCalculation');
            crp = crp.stopCalculation();
        }
    };

    onMount(() => {
        $NotificationSystem.start(reloadData);
        return () => {
            $NotificationSystem.stop();
        };
    });

    function redirectToConnectionList() {
        console.log('redirectToConnectionList');
        let userConfirmation = true;
        if (Object.keys($filter.connections.queries).length > 0 || $filter.connections.quickSearch !== '') {
            userConfirmation = confirm($_('Are you sure you want to go to the connections list? All filters will be removed.'));
        }
        let q = { outdated: { $in: ['true'] } };
        if (userConfirmation) {
            $filter.connections.quickSearch = '';
            $filter.connections.queries = q; // FOR SURE
            let filterManagerIO = new CustomFilterManagerIO(get(api), $filter.connections.managerStorageKey);
            let f = new Filter();
            f.setFilterQueries(q);
            let fl = new FilterLastUsed();
            let fm = new FilterManager(filterManagerIO);
            fm.setFilters(f, fl);
            fm.saveToLocalStorage().catch(error => {
                console.error('redirectToConnectionList, saveToLocalStorage', error);
            });
            fm.loadFromLocalStorage({ loadMainFilter: true, loadLastUsedFilter: false }).finally(() => {
                console.log('redirectToConnectionList, loaded', fm.getMainFilter());
                $routes.forceReload = true;
                $routes.forceReloadFilter = true;
                Navigation.navigate('/connections');
            });
        }
    }

    function redirectToLocationList() {
        let userConfirmation = true;
        if (Object.keys($filter.jobsToBeCompleted.queries).length > 0 || $filter.jobsToBeCompleted.quickSearch !== '') {
            userConfirmation = confirm($_('Are you sure you want to go to the points list? The currently set filters will be removed.'));
        }
        let q = { coords_accepted: { $in: ['false'] } };
        if (userConfirmation) {
            $filter.jobsToBeCompleted.quickSearch = '';
            $filter.jobsToBeCompleted.queries = q;
            let filterManagerIO = new CustomFilterManagerIO(get(api), $filter.jobsToBeCompleted.managerStorageKey);
            let f = new Filter();
            f.setFilterQueries(q);
            let fl = new FilterLastUsed();
            let fm = new FilterManager(filterManagerIO);
            fm.setFilters(f, fl);
            fm.saveToLocalStorage().catch(error => {
                console.error('redirectToLocationList, saveToLocalStorage', error);
            });
            fm.loadFromLocalStorage({ loadMainFilter: true, loadLastUsedFilter: false }).finally(() => {
                console.log('redirectToLocationList, loaded', fm.getMainFilter());
                $jobs.forceReload = true;
                $jobs.forceReloadFilter = true;
                Navigation.navigate('/');
            });
        }
    }

    $: {
        transactionLimitExceeded = '';
        if (!$app.settingsReadonly?.allow_limit_exceeding && $app.settingsReadonly?.max_transactions_per_month !== null) {
            if ($routes.currentTransactionCount.count >= $app.settingsReadonly?.max_transactions_per_month) {
                transactionLimitExceeded = 'transaction_limit_exceeded';
            } else if ($routes.currentTransactionCount.count + context?.countRoutesToCalculate > $app.settingsReadonly?.max_transactions_per_month) {
                transactionLimitExceeded = 'transaction_limit_will_be_exceeded';
            }
        }
    }
</script>

<div class="monitoring-system-container">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="monitoring-system-item" transition:fade={{ duration: 300 }} on:click={redirectToLocationList}>
        {#if context && context.countNotGeocodedLocations > 0}
            <div class="info" title={$_('Ungeocoded points')}>
                <div class="description">
                    <span>{$_('Ungeocoded points')}:</span>
                </div>
                <div class="value">{context?.countNotGeocodedLocations}</div>
            </div>
        {/if}
    </div>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="monitoring-system-item" transition:fade={{ duration: 300 }} on:click={redirectToConnectionList}>
        {#if crp.showProgressBar()}
            <div style="width: 200px; padding: 0 10px;">
                <ProgressBar current={progressPercent} withShadow={false} label={$_('Obliczanie tras')} />
            </div>
        {:else if crp.showCalcButton()}
            <!-- tooltip na obudowie, bo wyłączony <button> nie wyświetla title w części przeglądarek -->
            <div class="info" title={$canWrite ? (transactionLimitExceeded ? $_(transactionLimitExceeded, {values: {limit: $app.settingsReadonly?.max_transactions_per_month}}) : $_('Trasy do obliczenia')) : $_(writeDisabledTooltip)}>
                <div class="description">
                    <span>{$_('Trasy do obliczenia')}:</span>
                </div>
                <div class="value">{context?.countRoutesToCalculate}</div>
                <ButtonSmallBlueRounded
                    text="Oblicz trasy"
                    disabled={!$canWrite || transactionLimitExceeded !== ''}
                    on:click={async () => {
                        // ViewConnection::calcRoutesNeededToCalculate jest na liście
                        // BLOCKED_CUSTOM_OPERATIONS - backend odda 403
                        if (!get(canWrite) || transactionLimitExceeded) {
                            return;
                        }
                        wasBusy = false;
                        crp = crp.startCalculation(() => {
                            console.log('crp.endCalculation');
                            $routes.forceReload = true;
                        });
                        Umami.event('route_calc_triggered', { count: context?.countRoutesToCalculate ?? 0 });
                        $NotificationSystem.changeIntervalSeconds(3);
                        $NotificationSystem.calcRoutesNeededToCalculate();
                        $NotificationSystem.reload();
                    }}
                />
            </div>
        {/if}
    </div>
</div>

<style>
    .monitoring-system-container {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        column-gap: 10px;
        padding: 5px 0;
    }
    .monitoring-system-item {
        display: flex;
        flex-direction: row;
        column-gap: 9px;
        cursor: pointer;
        min-height: 35px;
        color: #3e352e;
        background-color: #f0f0f0;
        border-radius: 6px;
    }
    .monitoring-system-item:hover {
        background-color: var(--analytics-gray);
    }
    .info {
        /*cursor: pointer;*/
        padding: 4px 6px;
        min-width: 120px;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        row-gap: 3px;
        column-gap: 10px;
    }

    .description {
        font-size: 12px;
    }

    .value {
        font-size: 14px;
        font-weight: 600;
        color: var(--planning-page-accent-color);
    }
</style>
